'use client';
import React, { useState } from 'react';
import { Send, AlertTriangle } from 'lucide-react';
import DiagnosticTimer from '@/app/diagnostic-test/components/DiagnosticTimer';

interface Props {
  onSubmit: () => void;
}

export default function MockExamInterface({ onSubmit }: Props) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [currentPassage, setCurrentPassage] = useState(1);

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Strict exam top bar */}
      <div className="flex-shrink-0 bg-foreground text-background px-5 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold">IELTS Academic Reading — Mock Exam 1</span>
          <span className="text-xs text-background/60">Computer-Delivered Simulation</span>
        </div>
        <DiagnosticTimer totalSeconds={3600} onExpire={onSubmit} />
        <button
          onClick={() => setShowConfirm(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-background text-sm font-semibold transition-colors"
        >
          <Send size={13} />
          Submit Test
        </button>
      </div>

      {/* Passage tabs */}
      <div className="flex-shrink-0 bg-card border-b border-border px-5 flex items-center gap-1 pt-2">
        {[1, 2, 3].map((p) => (
          <button
            key={`mock-passage-${p}`}
            onClick={() => setCurrentPassage(p)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${
              currentPassage === p
                ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Passage {p} · Q{p === 1 ? '1–13' : p === 2 ? '14–26' : '27–40'}
          </button>
        ))}
        <div className="ml-auto text-xs text-muted-foreground pb-2">
          Exam in progress — no hints available
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex min-h-0 items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📄</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground mb-2">Passage {currentPassage} — Full Exam Mode</h2>
          <p className="text-sm text-muted-foreground mb-4">
            In a full implementation, this panel renders the complete passage and question set for Passage {currentPassage} under strict 60-minute exam conditions.
          </p>
          <p className="text-xs text-muted-foreground bg-muted px-3 py-2 rounded-lg">
            {/* Backend integration point: Load full passage and question set from question bank API */}
            Questions are loaded from the tagged question bank — filtered by difficulty index to match real exam calibration.
          </p>
          <button onClick={() => setShowConfirm(true)} className="btn-primary mt-6">
            Submit Mock Exam & View Report →
          </button>
        </div>
      </div>

      {/* Submit confirm */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm animate-fade-in">
          <div className="card-base p-6 max-w-md w-full mx-4 animate-scale-in shadow-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-warning-bg flex items-center justify-center">
                <AlertTriangle size={20} className="text-warning" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Submit Mock Exam?</h3>
                <p className="text-xs text-muted-foreground">Your performance report will be generated immediately</p>
              </div>
            </div><p className="text-sm text-muted-foreground mb-5 leading-relaxed">
              Once submitted, your raw score will be converted to a band estimate and a full question-type breakdown report will be generated — including your gap to Band 8.0 and a last-mile revision plan.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirm(false)} className="btn-outline flex-1 justify-center">
                Return to Exam
              </button>
              <button onClick={onSubmit} className="btn-primary flex-1 justify-center">
                Submit & View Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}